<template>
	<div class="local">
		<div class="main">
			<transition name="fadeloading">
				<div v-if="!isLoaded" class="loading-wrapper">
					<div :class="getLoadingClass">
						<div class="loading__bar">
							<div class="loading__percent" :style="percentageLoaded"></div>
						</div>
						<div class="loading__msg" v-html="getLoadingMsg"></div>
					</div>
				</div>
			</transition>
			<div class="module module--filters">
				<div class="module__body">
					<div class="container">
						<div class="wrapper">
							<div class="grid grid--filters">
								<div class="grid__item">
									<h2 v-html="_translate('local.filters.title')"></h2>
									<form>
										<Dropdown id="select-locale" :data="getLocalesList" :label="_translate('local.filters.city.label')"
											:selection="getLocalesSelection" :display="getULocalesDisplay" @dropdownChange="setUpdatedLocale"
											searchable />
										<Dropdown v-if="hasPeriods" id="select-period" :data="getPeriodsList"
											:label="_translate('local.filters.date.label')" :selection="getPeriodsSelection"
											:display="getPeriodsDisplay" @dropdownChange="setUpdatedPeriod" />
										<div class="btn-wrap"><button class="btn btn--secondary" @click="getFilterRoute($event)"
												:disabled="getFilterButtonActive" v-text="getUpdateButtonText"></button></div>
									</form>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<div id="searchtrends" class="module module--local module--usage">
				<div class="module__body">
					<div class="container">
						<div class="wrapper">
							<h2 v-html="getSectionHeading('local.searchtrends.title')"></h2>

							<!-- Top Search Filters -->
							<section id="topsearchfilters"
								v-if="showLocalSection(['searchFilters.priceMax', 'searchFiltersB.commercialPropertyType'])">
								<div class="intro">
									<h3 v-html="_translate('local.searchtrends.filters')"></h3>
									<p v-html="_translate('local.searchtrends.filters.intro')"></p>
								</div>
								<div class="">
									<Card>
										<template v-slot:front>
											<Dropdown id="select-filters" v-if="isTablet" :data="getSearchDropdwonData" label=""
												:selection="getActiveSearchSelection" :display="getActiveSearchFilter"
												@dropdownChange="setTermsTableDropdown" />
											<div v-else class="filters-tabs">
												<button class="filters-tab-ctrl prev" @click="advanceTermsTable(true)"><span
														v-html="_translate('Previous')"></span></button>
												<button v-for="(option, key) in searchCategories" :class="getFilterButtonClass(key)"
													@click="setTermsTable(key)"><span v-html="option.text"></span></button>
												<button class="filters-tab-ctrl next" @click="advanceTermsTable()"><span
														v-html="_translate('Next')"></span></button>
											</div>
											<div class="filters-tables">
												<transition name="fade">
													<TermList v-if="showTermsTable('priceMin')" id="priceMin" :data="getPriceMin" title="false"
														price full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('priceMax')" id="priceMax" :data="getPriceMax" title="false"
														price full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('bedrooms')" id="bedrooms" :data="getBedrooms" title="false"
														full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('bathrooms')" id="bathrooms" :data="getBathrooms" title="false"
														full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('landSize')" id="landSize" :data="getLandSize" title="false"
														full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('buildingType')" id="buildingType" :data="getBuildingType"
														title="false" full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('residentialPropertyType')" id="resProperty"
														:data="getResidentialPropertyType" title="false" full />
												</transition>
												<transition name="fade">
													<TermList v-if="showTermsTable('commercialPropertyType')" id="comProperty"
														:data="getCommercialPropertyType" title="false" full />
												</transition>
											</div>
										</template>
									</Card>
								</div>
							</section>

							<!-- Top Search Terms -->
							<section id="topsearchterms"
								v-if="showLocalSection(['searchFiltersB.geographicSearchTerms', 'searchFiltersB.keywordSearchTerms'])">
								<div class="intro">
									<h3 v-html="_translate('local.searchtrends.terms')"></h3>
									<p v-html="_translate('local.searchtrends.terms.intro')"></p>
								</div>
								<div class="grid grid--2-1 grid--gap">
									<Card>
										<template v-slot:front>
											<TermList id="keywordTerms" :data="getKeywordSearchTerms"
												:title="_translate('local.searchtrends.keyword.title')"
												:description="_translate('local.searchtrends.keyword.intro')" />
										</template>
									</Card>
									<Card>
										<template v-slot:front>
											<TermList id="geoTerms" :data="getGeographicSearchTerms"
												:title="_translate('local.searchtrends.geographic.title')"
												:description="_translate('local.searchtrends.geographic.intro')" />
										</template>
									</Card>
								</div>
							</section>

							<!-- Neighbourhoods -->
							<section id="neighbourhoods" v-if="showNeighbourhoods">
								<div class="intro">
									<h3 v-html="_translate('local.neighbourhoods.title')"></h3>
									<p v-html="getLocalizedHeading('local.neighbourhoods.intro') + '.'"></p>
								</div>
								<div class="">
									<Neighbourhoods :title="_translate('local.neighbourhoods.heading')" :data="getNeighbourhoodsData" />
								</div>
							</section>

							<!-- Provinces -->
							<section id="provinces" v-if="showProvincial">
								<div class="intro">
									<h3 v-html="_translate('local.provincial.title')"></h3>
									<p v-html="getLocalizedHeading('local.provincial.intro')"></p>
								</div>
								<div class="">
									<Provinces :data="getProvincialData" />
								</div>
							</section>

						</div>
					</div>
				</div>
			</div>
			<div id="demographics" class="module module--local module--usage">
				<div class="module__body">
					<div class="container">
						<div class="wrapper">
							<h2 v-html="getSectionHeading('local.demographics.title')"></h2>
							<section v-if="showLocalSection(['demographics.deviceBreakdown'])">
								<div class="intro">
									<p v-html="_translate('local.demographics.intro')"></p>
								</div>
								<div class="grid grid--2-1-2 grid--gap">
									<Card>
										<template v-slot:front>
											<BarChart :heading="_translate('m.local.age')" color="red" :data="getUserAgeData"
												:basis="getUserAgeBasis(50)" percentage nbh />
										</template>
									</Card>
									<Card v-if="hasPersonaData">
										<template v-slot:front>
											<BarChart :heading="_translate('m.local.personas')" color="blue" :data="getPersonaData"
												:basis="getPersonasBasis(100000)" percentage />
										</template>
									</Card>
									<Card v-if="hasShortTermIntentionData">
										<template v-slot:front>
											<BarChart :heading="_translate('m.local.shortTermIntention')" color="blue"
												:data="getShortTermIntentionData" :basis="getShortTermIntentionBasis(50)" percentage />
										</template>
									</Card>
									<Card class="pair">
										<template v-slot:front>
											<h4 v-html="_translate('m.local.gender')"></h4>
											<div class="card__pair">
												<div class="card__pair-item">
													<Illustration class="narrow" name="gender-f" />
													<div class="card__details">
														<p class="stat"><span v-html="getGender('female')" class="stat__number"></span></p>
														<p v-html="_translate('m.local.gender.female')"></p>
													</div>
												</div>
												<div class="card__pair-item">
													<Illustration class="narrow" name="gender-m" />
													<div class="card__details">
														<p class="stat"><span v-html="getGender('male')" class="stat__number"></span></p>
														<p v-html="_translate('m.local.gender.male')"></p>
													</div>
												</div>
											</div>
											<p class="disclaimer" v-html="_translate('m.local.gender.disclaimer')"></p>
										</template>
									</Card>
								</div>
							</section>
						</div>
					</div>
				</div>
			</div>
			<div id="usagetrends" class="module module--local module--usage">
				<div class="module__body">
					<div class="container">
						<div class="wrapper">
							<h2 v-html="getSectionHeading('local.usage.title')"></h2>

							<!-- Device Breakdown -->
							<section id="devicebreakdown" v-if="showLocalSection(['demographics.deviceBreakdown'])">
								<div class="intro">
									<h3 v-html="_translate('local.usage.devices')"></h3>
									<p v-html="_translate('local.usage.devices.intro')"></p>
								</div>
								<div class="grid grid--3-1 grid--gap">
									<Card>
										<template v-slot:front>
											<Illustration name="device-desktop" />
											<p class="stat stat--stacked"><span class="stat__number"
													v-html="getDeviceBreakdownPercentage('desktop')"></span> {{
												_translate('local.usage.devices.desktop') }}</p>
										</template>
									</Card>
									<Card>
										<template v-slot:front>
											<Illustration name="device-phone" />
											<p class="stat stat--stacked"><span class="stat__number"
													v-html="getDeviceBreakdownPercentage('mobile')"></span> {{
												_translate('local.usage.devices.mobile') }}</p>
										</template>
									</Card>
									<Card>
										<template v-slot:front>
											<Illustration name="device-tablet" />
											<p class="stat stat--stacked"><span class="stat__number"
													v-html="getDeviceBreakdownPercentage('tablet')"></span> {{
												_translate('local.usage.devices.tablet') }}</p>
										</template>
									</Card>
								</div>
							</section>

							<!-- Social Sharing -->
							<section id="socialsharing" v-if="showLocalSection(['socialShares.shareDevices'])">
								<div class="intro">
									<h3 v-html="_translate('local.usage.socialshare')"></h3>
									<p v-html="_translate('local.usage.socialshare.intro')"></p>
								</div>
								<div class="grid grid--1-2-2 grid--gap">
									<Card>
										<template v-slot:front>
											<div class="grid grid--devices">
												<div class="grid__item">
													<BarChart :heading="_translate('local.social.sharing.devices')" color="blue"
														:data="getShareDevices" :basis="getShareDevices.max*1.15" percentage />
												</div>
												<div class="grid__item grid__item--v-center">
													<Illustration name="device-source-hub" />
												</div>
											</div>
										</template>
									</Card>
									<Card>
										<template v-slot:front>
											<BarChart :heading="_translate('local.social.sharing.listings')" color="red"
												:data="getShareListings" :basis="getShareListings.max*1.15" percentage social />
										</template>
									</Card>
									<Card>
										<template v-slot:front>
											<BarChart :heading="_translate('local.social.sharing.nonlistings')" color="grey"
												:data="getShareNonListings" :basis="getShareNonListings.max*1.15" percentage social />
										</template>
									</Card>
								</div>
							</section>
						</div>
					</div>
				</div>
			</div>
		</div>
		<Footer :isTablet="isTablet" bgbox local>
			<template v-slot:details>
				<h2 v-html="_translate('report.footer.report.title')"></h2>
				<p v-html="footerContent"></p>
				<p v-html="_translate('local.footer.paragraph2')"></p>
				<div class="share">
					<span class="share__label" v-html="_translate('report.intro.share')"></span>
					<SocialShare platform="email" />
					<SocialShare platform="facebook" />
					<SocialShare platform="twitter" />
					<SocialShare platform="linkedin" />
				</div>
			</template>
			<template v-slot:cta>
				<p v-html="_translate('local.footer.report.insights.text')"></p>
				<Button :href="getReportPath" :text="_translate('local.footer.report.insights.button')" type="secondary"
					inline></Button>
			</template>
		</Footer>
	</div>
</template>

<script>

	import BarChart from '@/components/BarChart.vue'
	import BgBox from '@/components/BgBox.vue'
	import SocialShare from '@/components/SocialShare.vue'
	import Button from '@/components/Button.vue'
	import Card from '@/components/Card.vue'
	import Dropdown from '@/components/Dropdown.vue'
	import Illustration from '@/components/Illustration.vue'
	import TermList from '@/components/TermList.vue'
	import Neighbourhoods from '@/components/Neighbourhoods.vue'
	import Provinces from '@/components/Provinces.vue'
	import Footer from '@/modules/Footer.vue'

	import objectFitImages from 'object-fit-images'

	export default {

	  name: 'report',

	  data: function() {
	  	return {

	  		data: {
	  			'empty': true,
	  			'complete': true,
	  			'completion': 0,
	  			'error': false,
	  		},

	  		backoff: 0,

	  		requestTimeout: false,

	  		loaded: false,

	  		locale: '',

	  		uLocale: '',

	  		period: '',

	  		uPeriod: '',

	  		locales: {},

				quarters: {},

				year: 2020,

	  		periods: {
					"Year2020": '2020',
	  		},

	  		searchFilters: 0,

	  		searchCategories: [
					{
						id: 'priceMin',
						text: this._translate('local.searchtrends.filters.pricemin'),
					},
					{
						id: 'priceMax',
						text: this._translate('local.searchtrends.filters.pricemax'),
					},
					{
						id: 'bedrooms',
						text: this._translate('local.searchtrends.filters.bedrooms'),
					},
					{
						id: 'bathrooms',
						text: this._translate('local.searchtrends.filters.bathrooms'),
					},
					{
						id: 'landSize',
						text: this._translate('local.searchtrends.filters.landsize'),
					},
					{
						id: 'buildingType',
						text: this._translate('local.searchtrends.filters.buildingtype'),
					},
					{
						id: 'residentialPropertyType',
						text: this._translate('local.searchtrends.filters.residentialpropertytype'),
					},
					{
						id: 'commercialPropertyType',
						text: this._translate('local.searchtrends.filters.commercialpropertytype'),
					},
	  		]
	  	};

	  },

	  components: {
	  	BgBox,
	  	BarChart,
	  	Button,
	  	SocialShare,
	  	Card,
	  	Dropdown,
	  	Illustration,
	  	TermList,
			Neighbourhoods,
			Provinces,
	  	Footer,
	  },

	  mounted: function() {

	  	this.$data.backoff = 0;

			if ( this.$route.params.locale ) {
				this.$data.locale = this.$route.params.locale
				this.$data.uLocale = this.$route.params.locale
			} else {
				this.$data.locale = 'canada'
				this.$data.uLocale = 'canada'
			}

			var locale_uri = window.api_uri + 'api/status/';
      this.axios.get(locale_uri)
        .then(response => {

        	this.$data.locales = response.data.locales;
        	this.$data.quarters = response.data.quarters;
					this.$data.year = response.data.year;

					if ( this.$route.params.period ) {
						this.$data.period = this.$route.params.period;
						this.$data.uPeriod = this.$route.params.period;

						// Prevent using an option that is not in the available quarters,
						// as pulled in from the status API
						if ( this.$data.uPeriod.length === 6 ) {
							if ( this.$data.uPeriod.substr(0,1) === 'q') {
								if ( ! this.$data.quarters[this.$data.uPeriod] ) {
									this.$data.period = response.data.default_period;
									this.$data.uPeriod = response.data.default_period;
								}
							}
						}

					} else {
						this.$data.period = response.data.default_period;
						this.$data.uPeriod = response.data.default_period;
					}

					this.$data.periods = {};
					if ( response.data.use_year !== 'false' ) {
						var periodName = 'Year' + this.$data.year;
						this.$data.periods[periodName] = this.$data.year;
					}

					this.getApiData();

        },

        response => {

         	// Set error.
          console.log(response.data);

        }

      );

	  	objectFitImages();

	  },

	  computed: {

	  	getDateRange: function() {
	  		return this.$data.data.start + ' to ' + this.$data.data.end;
	  	},

	  	getReportPath: function() {
	  		return '/';
	  	},

	  	outputData: function() {

	  		return JSON.stringify(this.$data.data, null, 2);

	  	},

	  	outputLocales: function() {

	  		return JSON.stringify(this.getLocalesList, null, 2);

	  	},

	  	outputPeriods: function() {

	  		return JSON.stringify(this.getPeriodsList, null, 2);

	  	},

	  	getTargetUrl: function() {

				var routePath = '/';

				if ( this.$data.uLocale !== '' ) {
					routePath += this.$data.uLocale;
				}

				// if ( this.$data.uPeriod ) {
				// 	routePath += '/' + this.$data.uPeriod;
				// }

				return routePath;

	  	},

	  	getLoadingMsg: function() {

	  		var msg = this._prints(this._translate('loading.msg'), this.getLocalesDisplay);

	  		if ( this.$data.data.error ) {
					msg = 'Error ' + this.$data.data.error.code + ': ' + this.$data.data.error.message;
					if ( this.$data.data.error.code === 900 ) {
						msg = this._prints(this._translate('error.missing.locale'), this.getLocalesDisplay);
					}
	  		}

	  		return msg;

	  	},

	  	getLocalesList: function() {

	  		var data = {};

	  		for ( var key in this.$data.locales ) {

	  			var locale_name = this._translate(key);

	  			data[key] = {
	  				'display': (locale_name === '[undefined string]') ? this.$data.locales[key] : locale_name,
	  				'query': this.buildQueryFrom('locale', key)
	  			};
	  		}

	  		return data;

	  	},

	  	getLocalesDisplay: function() {

	  		if ( ! this.$data.locale ) {
	  			return 'Canada';
	  		}

	  		var locale_name = this._translate(this.$data.locale);

				return (locale_name === '[undefined string]') ? this.$data.locales[this.$data.locale] : locale_name;

	  	},

	  	getULocalesDisplay: function() {

	  		if ( ! this.$data.uLocale ) {
	  			return 'Canada';
	  		}

	  		var locale_name = this._translate(this.$data.uLocale);

				return (locale_name === '[undefined string]') ? this.$data.locales[this.$data.uLocale] : locale_name;

	  	},

	  	getLocalesSelection: function() {

	  		return this.$data.uLocale;

	  	},

			getUpdateButtonText: function() {
				var key = (this.hasPeriods) ? 'local.filters.button.update' : 'local.filters.button.updatecity';
				return this._translate(key);
			},

			hasPeriods: function() {

				return (Object.keys(this.getPeriodsList).length > 1)

			},

	  	getPeriodsList: function() {

	  		var data = {}, label;

	  		for ( var key in this.$data.periods ) {
	  			data[key] = {
	  				'display': this.$data.periods[key],
	  				'query': this.buildQueryFrom('period', key)
	  			};
	  		}

	  		for ( var key in this.$data.quarters ) {
					label = this.getQuarterName(key);
	  			data[key] = {
	  				'display': label,
	  				'query': this.buildQueryFrom('period', key)
	  			};
	  		}

	  		return data;

	  	},

	  	getPeriodsDisplay: function() {

	  		if ( ! this.$data.uPeriod ) {
	  			return this._translate('local.filters.3months');
	  		}

				var quarters = ['q1','q2','q3','q4'];
				if ( this.$data.uPeriod.length === 6 ) {
					return this.getQuarterName(this.$data.uPeriod);
				} else if ( this.$data.uPeriod.substr(0,4) === 'Year' ) {
					return this.$data.uPeriod.substr(4);
				}

	  		var locale_name = this._translate('local.filters.' + this.$data.uPeriod.toLowerCase());

				return (locale_name === '[undefined string]') ? this.$data.locales[this.$data.uPeriod] : locale_name;

	  	},

	  	getPeriodsSelection: function() {

	  		return this.$data.uPeriod;

	  	},

	  	getFilterButtonActive: function() {

	  		// If no change in filters has been selected we ALSO
	  		// want to disable the button
	  		if ( this.$data.period === this.$data.uPeriod &&
	  		     this.$data.locale === this.$data.uLocale ) {
	  			return true;
	  		}

	  		// // If data is not loaded we want to disable the button
	  		// if ( ! this.isLoaded ) {
	  		// 	return true;
	  		// }				

	  		// Otherwise, let's return false
	  		return false;

	  	},

	  	isLoaded: function() {

	  		return this.$data.data.complete;

	  	},

			showNeighbourhoods: function() {
				if ( this.$data.data.listingArea ) {
					if ( this.$data.data.listingArea.listing_neighbourhood ) {
						return true;
					}
				}
				return false;
			},

			showProvincial: function() {
				if ( this.$data.data.listingArea ) {
					if ( this.$data.data.listingArea.listing_province ) {
						return true;
					}
				}
				return false;
			},			

	  	getLoadingClass: function() {

	  		var c = 'loading'

	  		if ( this.$data.data.error ) {
	  			c += ' error';
	  		}

	  		return c;

	  	},

	  	percentageLoaded: function() {

	  		if ( this.isLoaded ) { return 'width: 100%' }

	  		if ( this.$data.data.completion ) {
	  			return 'width: ' + this.$data.data.completion + '%';
	  		} else {
	  			return 'width: 0%';
	  		}

	  	},

	  	getFilterTag: function() {

	  		return this.getLocalesDisplay;

	  	},

	  	getActiveSearchFilter: function() {

	  		return this.$data.searchCategories[this.$data.searchFilters].text;

	  	},

	  	getActiveSearchSelection: function() {

	  		return this.$data.searchCategories[this.$data.searchFilters].id;

	  	},

	  	getSearchDropdwonData: function() {

	  		var obj = {};

	  		for ( var x = 0; x < this.$data.searchCategories.length; x++ ) {
	  			var label = this.$data.searchCategories[x]['text']
	  			obj[this.$data.searchCategories[x]['id']] = label.replace('&nbsp;', ' ');
	  		}

	  		return obj;

	  	},

	  	// API Data Objects

	  	getGeographicSearchTerms() {

	  		if ( this.$data.data.searchFiltersB ) {
		  		if ( this.$data.data.searchFiltersB.geographicSearchTerms ) {
		  			if ( this.$data.data.searchFiltersB.geographicSearchTerms.data ) {
		  				return this.$data.data.searchFiltersB.geographicSearchTerms;
		  			}
		  		}
	  		}

	  		return {};

	  	},

	  	getKeywordSearchTerms() {

	  		if ( this.$data.data.searchFiltersB.keywordSearchTerms ) {
	  			if ( this.$data.data.searchFiltersB.keywordSearchTerms.data ) {
	  				return this.$data.data.searchFiltersB.keywordSearchTerms;
	  			}
	  		}

	  		return {};

	  	},

	  	getNeighbourhoods() {

	  		if ( this.$data.data.demographics.neighbourhoods ) {
	  			if ( this.$data.data.demographics.neighbourhoods.data ) {
	  				return this.$data.data.demographics.neighbourhoods;
	  			}
	  		}

	  		return {};

	  	},

	  	getPriceMin() {

	  		if ( this.$data.data.searchFilters.priceMin ) {
	  			if ( this.$data.data.searchFilters.priceMin.data ) {
	  				return this.$data.data.searchFilters.priceMin;
	  			}
	  		}

	  		return {};

	  	},

	  	getPriceMax() {

	  		if ( this.$data.data.searchFilters.priceMax ) {
	  			if ( this.$data.data.searchFilters.priceMax.data ) {
	  				return this.$data.data.searchFilters.priceMax;
	  			}
	  		}

	  		return {};

	  	},

	  	getBedrooms() {

	  		if ( this.$data.data.searchFilters.bedrooms ) {
	  			if ( this.$data.data.searchFilters.bedrooms.data ) {
	  				return this.$data.data.searchFilters.bedrooms;
	  			}
	  		}

	  		return {};

	  	},

	  	getBathrooms() {

	  		if ( this.$data.data.searchFilters.bathrooms ) {
	  			if ( this.$data.data.searchFilters.bathrooms.data ) {
	  				return this.$data.data.searchFilters.bathrooms;
	  			}
	  		}

	  		return {};

	  	},

	  	getBuildingType() {

	  		if ( this.$data.data.searchFilters.buildingType ) {
	  			if ( this.$data.data.searchFilters.buildingType.data ) {
	  				return this.$data.data.searchFilters.buildingType;
	  			}
	  		}

	  		return {};

	  	},

	  	getLandSize() {

	  		if ( this.$data.data.searchFiltersB.landSize ) {
	  			if ( this.$data.data.searchFiltersB.landSize.data ) {
	  				return this.$data.data.searchFiltersB.landSize;
	  			}
	  		}

	  		return {};

	  	},

	  	getResidentialPropertyType() {

	  		if ( this.$data.data.searchFiltersB.residentialPropertyType ) {
	  			if ( this.$data.data.searchFiltersB.residentialPropertyType.data ) {
	  				return this.$data.data.searchFiltersB.residentialPropertyType;
	  			}
	  		}

	  		return {};

	  	},

	  	getCommercialPropertyType() {

	  		if ( this.$data.data.searchFiltersB.commercialPropertyType ) {
	  			if ( this.$data.data.searchFiltersB.commercialPropertyType.data ) {
	  				return this.$data.data.searchFiltersB.commercialPropertyType;
	  			}
	  		}

	  		return {};

	  	},

			getNeighbourhoodsData() {
				return this.$data.data.listingArea.listing_neighbourhood;
			},

			getProvincialData() {
				return this.$data.data.listingArea.listing_province;
			},			

	  	getShareDevices() {

				return this.getSocialShares('shareDevices');

	  	},

	  	getShareListings() {

				return this.getSocialShares('shareListings');

	  	},

	  	getShareNonListings() {

				return this.getSocialShares('shareNonListings');

	  	},

	  	getUserAgeData: function() {

	  		var data = {},
	  		    keys = ["18-24", "25-34", "35-44", "45-54", "55-64", "65+", "unknown"]

	  		// We basically want to use the keys array to force the order
	  		// that we output data in.
	  		if ( this.$data.data.demographics.age ) {

	  			if ( this.$data.data.demographics.age.data ) {

	  				for ( var index in keys ) {
	  					var key = keys[index];
	  					if (this.$data.data.demographics.age.data['_' + key] ) {
	  						data[key] = [this.$data.data.demographics.age.data['_' + key]];
	  					}
	  				}

	  			}
	  		}

		  	return {
					data: data,
					total: this.$data.data.demographics.age.total
				}

	  	},

	  	getPersonaData: function() {

	  		var data = {};

	  		// We basically want to use the keys array to force the order
	  		// that we output data in.
	  		if ( this.$data.data.demographics.personas ) {

	  			if ( this.$data.data.demographics.personas.data ) {

	  				for ( var key in this.$data.data.demographics.personas.data ) {
	  					if (this.$data.data.demographics.personas.data[key] && key !== '_No Preference') {
								if ( key === '_First time buyer' || key === '_1st time buyer') {
									if ( data['_1st time buyer'] ) {
										data['_1st time buyer'][0] += this.$data.data.demographics.personas.data[key];
									} else {
										data['_1st time buyer'] = [this.$data.data.demographics.personas.data[key]];
									}
								} else if ( key === '_Repeat buyer' || key === '_Repeat Buyer') {
									if ( data['_Repeat buyer'] ) {
										data['_Repeat buyer'][0] += this.$data.data.demographics.personas.data[key];
									} else {
										data['_Repeat buyer'] = [this.$data.data.demographics.personas.data[key]];
									}
								} else {
	  							data[this.apiLabel(key)] = [this.$data.data.demographics.personas.data[key]];
								}
	  					}
	  				}

	  			}
	  		}

		  	return {
					data: data,
					total: this.$data.data.demographics.personas.total
				}

	  	},

			hasPersonaData: function() {
				return this.$data.data.demographics.personas;
			},

			getShortTermIntentionData: function () {

				var data = {};

				// We basically want to use the keys array to force the order
				// that we output data in.
				if (this.$data.data.demographics.shortTermIntention) {

					if (this.$data.data.demographics.shortTermIntention.data) {

						for (var key in this.$data.data.demographics.shortTermIntention.data) {
							data[key] = [this.$data.data.demographics.shortTermIntention.data[key]];
						}

					}
				}

				return {
					data: data,
					total: this.$data.data.demographics.shortTermIntention.total
				}

			},			

			hasShortTermIntentionData: function () {
				return this.$data.data.demographics.shortTermIntention;
			},			

      footerContent: function() {

        var content  = this._translate('local.footer.paragraph1'),
            linkText = this._translate('qualtrics.linktext'),
            linkUrl  = this._translate('qualtrics.prod.link')

				if ( window.appEnv === 'staging' ) {
					linkUrl = this._translate('qualtrics.qa.link')
				} else if (window.appEnv === 'dev') {
					linkUrl = this._translate('qualtrics.dev.link')
				}

				// content = content.replace(linkText, '<a href="' + linkUrl + '" target="_blank">' + linkText + '</a>');
				content = content.replace(linkText, '<button class="link" id="briefsurvey">' + linkText + '</button>');
        return content;

      }

	  },

	  methods: {

	  	setUpdatedLocale(atts) {
	  		this.$data.uLocale = atts.key;
	  	},

	  	setUpdatedPeriod(atts) {
	  		this.$data.uPeriod = atts.key;
	  	},

	  	buildQueryFrom: function(type = 'locale', key) {

	  		var query = '/local/';

	  		if ( type === 'locale' ) {

	  			query += key + '/';

	  			if ( this.$data.period ) {

	  				query += this.$data.period + '/';

	  			}

	  		} else if ( type === 'period' ) {

	  			if ( this.$data.locale ) {

	  				query += this.$data.locale + '/';

	  			}

	  			query += key + '/';

	  		}

	  		return query;

	  	},

			getFilterRoute: function($e) {

				clearTimeout(window.apiRequestTimer);
	  		window.apiRequestTimer = false;

				$e.preventDefault();
				var routePath = this.getTargetUrl;

				// if ( this.$data.uLocale !== 'canada' ) {
				// 	routePath += '/' + this.$data.uLocale;
				// }

				if ( this.$data.uPeriod ) {
					routePath += '/' + this.$data.uPeriod;
				}

				if ( routePath !== this.$route.path ) {
					this.$data.data.complete = false;
					this.$router.push({ path: routePath });
				}

			},

	  	getApiData: function() {

	  		clearTimeout(window.apiRequestTimer);
	  		window.apiRequestTimer = false;

				var request = {},
            request_uri = window.api_uri + 'api/',
						q = false;

				// Define our locale query string
        if ( this.$data.locale ) {
        	request_uri += '?locale=' + this.$data.locale;
        	q = true;
        }

        // Define our period query string
        if ( this.$data.period ) {
        	if ( ! q ) { request_uri += '?' } else { request_uri += '&'; }
        	request_uri += 'period=' + this.$data.period;
        }

        var self = this;
        setTimeout(function(){
        	if ( self.$data.data.completion < 100 ) {
	        	// console.warn('testing completion');
        		self.$data.data.complete = false;
        	}
        }, 1000);

        // Now we execute our AJAX request
        this.axios.get(request_uri)
          .then(response => {

				  	if ( this.$route.name ) {
				  		this.$cookies.set('local_' + window.ccode, this.$route.path, '1y');
				  		this.$emit('routed', this.$route.path);
				  	}

  	        var errored = false;

  	        if ( response.data.error ) {
  	        	/*if ( Number(response.data.error.code) === 429 ) {
  	        		this.$data.backoff += 2000;
  	        		response.data.error = false;
  	        	} else */ if (Number(response.data.error.code) === 929 ) {
  	        		this.$data.backoff += 1000;
  	        		response.data.error = false;
  	        	} else {
  	        		errored = true;
  	        	}
  	        }

          	this.$data.data = response.data;
						this.$root.cPercentage = response.data.calculate_percentage;

          	if ( response.data.complete ) {
        			this.$data.loaded = true;
        		} else {
        			if ( ! errored && ! this.isReport ) {
	        			var _this = this,
	        			    delay = 2500 + this.$data.backoff;

	        			window.apiRequestTimer = setTimeout(function() {
	        				_this.getApiData();
	        			}, delay);
        			}
        		}

          },

          response => {

           	// Set error.
            console.log(response.data);

          }

        );

	  	},

			getQuarterName: function(key) {

				var quarter = this.getQuarterOrdinal(Number(key.substr(1,1)));
				return  quarter + ' ' + key.substr(2);

			},

			getQuarterOrdinal: function(quarter) {

				switch(quarter) {
					case 1:
						return this._translate("local.dates.quarter.first");
						break;
					case 2:
                        return this._translate("local.dates.quarter.second");
                        break;
					case 3:
                        return this._translate("local.dates.quarter.third");
                        break;
					case 4:
                        return this._translate("local.dates.quarter.fourth");
                        break;
				}

			},

			getSocialShares(section) {

				var data = {};
				let max = 0;
				if ( this.$data.data.socialShares[section] ) {
	  			if ( this.$data.data.socialShares[section].data ) {
	  				for ( var key in this.$data.data.socialShares[section].data ) {
							let val = Number(this.$data.data.socialShares[section].data[key]);
							data[key] = [val];
							if ( val > max ) {
								max = val;
							}
						}
	  			}
	  		}

				return { 
				 data: data,
				 total: this.$data.data.socialShares[section].total,
				 max: max,
				}

			},

	  	showLocalSection: function(keys) {

	  		for ( var key in keys ) {

	  			var k = keys[key].split('.');

	  			if ( k.length === 2 ) {

	  				if ( ! this.$data.data[k[0]] ) {
	  					return false;
	  				}
		  			if ( ! this.$data.data[k[0]][k[1]] ) {
		  				return false;
		  			}

	  			}
	  		}

	  		return true;

	  	},

	  	setTermsTable: function(id) {

	  		return (this.$data.searchFilters = id )

	  	},

	  	setTermsTableDropdown: function(atts) {

	  		var key = atts.key,
	  		    index = 0;

	  		for ( var x = 0; x < this.$data.searchCategories.length; x++ ) {
	  			if ( this.$data.searchCategories[x].id === key ) {
	  				index = x;
	  			}
	  		}

	  		return (this.$data.searchFilters = index )

	  	},

	  	advanceTermsTable: function(reverse) {

	  		if ( reverse === null ) {
	  			reverse = false;
	  		}

	  		var id = this.$data.searchFilters,
	  		    length = this.$data.searchCategories.length,
	  		    newId = (reverse) ? id - 1 : id + 1;

	  		if ( newId >= length ) { newId = 0; }
	  		else if ( newId < 0 ) { newId = length - 1 }

				this.$data.searchFilters = newId;

	  	},

			showTermsTable: function(id) {

				return (this.$data.searchCategories[this.$data.searchFilters].id === id )

			},

			getFilterButtonClass: function(id) {

				var c = "filters-tab";
				if ( this.$data.searchFilters === id ) {
					c += ' js-active';
				}
				return c;

			},

	  	getSectionHeading: function(token) {

	  		var heading = this._translate(token);

	  		if ( this.getLocalesSelection ) {

	  			if ( this.getLocalesSelection !== 'canada' && this.getLocalesDisplay ) {

	  				var delimiter = ( window.appLang === 'fr' ) ? ' dans ' : ' in ';
	  				heading += delimiter + ' <span>' + this.getLocalesDisplay + '</span>';

	  			}

	  		}

	  		return heading;

	  	},

			getLocalizedHeading: function(token) {
				var heading = this._translate(token);
				if ( this.getLocalesDisplay ) {
					return this._prints(heading, this.getLocalesDisplay);
				}
				return heading;
			},


	  	/**
	  	 * USAGE TRENDS
	  	 * The methods contained here are designed to help get and format the data
	  	 * from the API requests for output within the report.
	  	 */

	  	getDeviceBreakdownPercentage: function(key) {

				let val = this.calculatePercentage ?
					Number(this.$data.data.demographics.deviceBreakdown.data['_' + key]) / Number(this.$data.data.demographics.deviceBreakdown.total) :
					Number(this.$data.data.demographics.deviceBreakdown.data['_' + key])/100;
	  		return this.formatPercentage(val, 1);

	  	},

	  	getGender: function(key) {

	  		if ( this.$data.data.demographics.gender.data['_' + key] ) {
					let val = this.calculatePercentage ? 
						(this.$data.data.demographics.gender.data['_' + key]/this.$data.data.demographics.gender.total) :
					  this.$data.data.demographics.gender.data['_' + key]/100;
	  			return this.formatPercentage(val);
	  		} else {
	  			return this.formatPercentage(0);
	  		}

	  	},

	  	getUserAgeBasis: function(basis) {

	  		if ( this.$data.data.demographics.age.data ) {
	  			basis = 1;
	  			for ( var key in this.$data.data.demographics.age.data ) {
	  				if ( this.$data.data.demographics.age.data[key] > basis ) {
	  					basis = this.$data.data.demographics.age.data[key];
	  				}
	  			}

	  			basis *= 1.35;

	  		}

	  		return basis;

	  	},

	  	getPersonasBasis: function(basis) {

	  		if ( this.$data.data.demographics.personas.data ) {
	  			basis = 1;
	  			for ( var key in this.$data.data.demographics.personas.data ) {
	  				if ( Number(this.$data.data.demographics.personas.data[key]) > basis ) {
	  					basis = Number(this.$data.data.demographics.personas.data[key]);
	  				}
	  			}

	  			basis *= 1.75;

	  		}

	  		return basis;

	  	},

			getShortTermIntentionBasis: function(basis) {

	  		if ( this.$data.data.demographics.shortTermIntention.data ) {
	  			basis = 1;
	  			for ( var key in this.$data.data.demographics.shortTermIntention.data ) {
	  				if ( Number(this.$data.data.demographics.shortTermIntention.data[key]) > basis ) {
	  					basis = Number(this.$data.data.demographics.shortTermIntention.data[key]);
	  				}
	  			}

	  			basis *= 1.5;

	  		}

	  		return basis;

	  	}			

	  }

	}

</script>

<style lang="scss">

	.hero--local {

    .bg-box {

      .wrapper {

        margin-bottom: 6rem;

        @include mq('tab') {

          margin-bottom: 6rem;

        }

        @include mq('lap') {

          margin-bottom: 9rem;

        }

      }

    }

  }

	.main { 
		padding: 0 1.5rem;
	}

	.module--filters {

		position: relative;
		z-index: 8;

		@include mq('tab') {

			margin-bottom: -3rem;

		}

		.grid--filters {

			.grid__item {

				// &:last-child {

				// 	@include mq('lap') {

				// 		display: flex;
				// 		align-items: bottom;
				// 		justify-content: center;

				// 	}

				// 	.illustration {

				// 		max-width: 230px;
				// 		width: 230px;
				// 		margin: 0;

				// 	}

				// }

			}

			.illustration {

				max-width: 260px;
				width: 260px;
				display: flex;
				margin: 0 auto;
				flex: auto;

				img {

					flex: 1;

				}

			}

		}

		.wrapper {
			margin-bottom: 0;
			background: $white;
			box-shadow: $box-shadow;
			margin-top: 2rem;
			padding: 3rem 1.5rem;
			margin-bottom: 3rem;

			@include mq('tab') {
				padding: 3rem;
				margin-bottom: 6rem;
			}

		}

		h2 {

			@include typesize('small');
			color: $blue-dark;
			margin-bottom: 3rem;

		}

		.illustration {

			max-width: 200px;
			margin: 0 auto 2rem;

		}

		form {

			@include mq('tab') {

				display: flex;

			}

			.dropdown,
			.btn-wrap {

				@include mq('tab') {
					width: 33.3333%;
				}

			}

			.btn-wrap {
				text-align: center;
				padding-top: 2em;

				@include mq('tab') {
					padding-top: 1.4em;
					padding-left: 1.4em;
				}
			}

			> :nth-child(1) {

				@include mq('tab') {
					margin-right: 1.5rem;
				}

			}

			> :nth-child(2) {

				margin-top: 3rem;

				@include mq('tab') {
					margin-left: 1.5rem;
					margin-top: 0;
				}

			}

		}

	}

	.module--local {

		h2 {

			@include typesize('extra-large');
			font-weight: 600;
			margin-top: 0;
			margin-bottom: 1.5em;

			@include mq('tab') {
				margin-top: 1em;
				margin-bottom: 3.75rem;
			}

			span {
				color: $red;
				font-weight: 300;
			}

		}

		.intro + .grid {

			margin-top: 2rem;

		}

		.wrapper {

			@include mq('tab') {
				margin-top: 0;
				margin-bottom: 0;
			}

		}

	}

	.filters-tables {
		position: relative;
		overflow: hidden;

	}

	.filters-tabs {
		display: flex;
		flex-wrap: wrap;
		padding: 0 3rem;
		position: relative;
	}

	.filters-tab {

		@include typesize('small');
		color: $grey;
		appearance: none;
		border: 0;
		background: none;
		font-weight: 300;
		outline: none;
		margin-bottom: 1em;
		display: inline-flex;
		flex-direction: column;
		align-items: center;
		padding: 0;
		cursor: pointer;

		span {

			display: flex;
			align-items: flex-start;
			justify-content: center;
			width: 100%;
			height: 100%;
			flex-basis: 1;
			padding: .5rem 1.25rem;

		}

		@include mq('tab') {
			width: 25%;
		}

		&.js-active {
			color: $blue-dark;
			font-weight: 400;
		}

	}

	.filters-tab-ctrl {

		font-size: 0;
		position: absolute;
		left: 0;
		top: 50%;
		appearance: none;
		background: none;
		border: 0;
		transform: translate3d(0, -50%, 0);
		outline: none;
		cursor: pointer;

		&:hover,
		&:focus,
		&:active {

			span {
				border-color: $blue-dark;
				transform: rotate(-45deg) scale(1);
			}

		}

		&.next {

			left: auto;
			right: 0;

			&:hover,
			&:focus,
			&:active {

				span { transform: rotate(135deg) scale(1); }

			}

			span { transform: rotate(135deg) scale(.85); }

		}

		span {

			display: inline-block;
			width: 1.5rem;
			height: 1.5rem;
			transform: rotate(-45deg) scale(.85);
			border-top: 1px solid $blue;
			border-left: 1px solid $blue;
			transition: transform .3s ease;

		}

	}

	.visitor-legend {
		font-size: .875rem;
		color: $red;
		height: .1rem;
		display: flex;
		align-items: center;
		height: 16px;
	}

	.visitor-icon {
		fill: $red-light;
		width: 14px;
		height: 14px;
		margin: 0 .35rem;
		position: relative;
		top: -1px;
		display: inline-block;
	}

	.loading-wrapper {
		position: fixed;
		bottom: 0;
		left: 0;
		right: 0;
		width: 100%;
		z-index: 10;
		@include mq('lap') {
			bottom: 1rem;
		}
	}

	.loading {

		margin: 0 auto;
		max-width: $wrapper + 2rem;
		background: $blue;
		@include typesize('tiny');
		color: $white;
		box-shadow: $box-shadow;
		transition: background-color .3s ease;

		@include mq('tab') {
			@include typesize('small');
		}

		&.error {
			background-color: $red;
			.loading__percent {
				background-color: $red-dark;
			}
		}

	}

	.loading__msg {
		padding: 1rem;
	}

	.loading__bar {
		background: $mist-dark;
	}

	.loading__percent {
		height: 5px;
		background: $blue-dark;
		transition: width .7s ease;
		transition: background-color .3s ease;
	}

</style>